import { computed, ComputedRef, ref, Ref } from "vue";
import { ICreditLineDetails } from "@/apps/credit-line/models/CreditLineDetails";
import { useStore } from "vuex";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { useRouter } from "vue-router";
import { isFeatureEnabled } from "@/apps/credit-line/helpers/summaryFeatures.helper";

const isLoading = ref(false);
const doesHaveEnoughBalanceToWithdraw = ref(false);
const lastFourDigits = ref<string | null>(null);
const isNewShipmentsUiEnabled = ref(false);
const isGoogleAutocompleteNotEnabled = ref(false);
let fetchPromise: Promise<ICreditLineDetails> | null = null;

export type IFetchSummary = (force?: boolean) => Promise<ICreditLineDetails>;

export function useCreditLineSummary(): {
  summary: ComputedRef<ICreditLineDetails | null>;
  isLoading: Ref<boolean>;
  fetchSummary: IFetchSummary;
  doesHaveEnoughBalanceToWithdraw: Ref<boolean>;
  lastFourDigits: ComputedRef<string | null>;
  isNewShipmentsUiEnabled: ComputedRef<boolean>;
  isGoogleAutocompleteNotEnabled: ComputedRef<boolean>;
} {
  const store = useStore();
  const router = useRouter();

  const summary = computed(() => store?.state?.creditLine?.summary || null);

  const fetchSummary = async (force = false) => {
    if (fetchPromise && !force) {
      return fetchPromise;
    }

    if (store.state?.creditLine?.summary && !force) {
      return Promise.resolve(store.state.creditLine.summary);
    }

    isLoading.value = true;
    fetchPromise = (async () => {
      try {
        await store.dispatch("creditLine/fetchSummary");

        const initialVirtualCards =
          store.getters["creditLine/getCardsByType"]("VIRTUAL").length;

        try {
          await store.dispatch("creditLine/executeServerRequestedActions");
        } catch (e) {
          ErrorHandler.handleNetworkErrorAndLog(e);
          console.log("Failed to execute serverRequestedActions", e);
        }

        const latestVirtualCards =
          store.getters["creditLine/getCardsByType"]("VIRTUAL").length;
        if (latestVirtualCards > initialVirtualCards) {
          await router.push({
            name: "CreditCardConfirmCreateView",
            query: { country: store.state?.creditLine?.summary.country },
          });
        }

        lastFourDigits.value =
          store.getters["creditLine/getActivePhysicalCard"]?.lastFour;

        doesHaveEnoughBalanceToWithdraw.value =
          store?.state?.creditLine?.summary?.availableCredit >=
          store?.state?.creditLine?.summary?.minWithdrawalAmount;

        isNewShipmentsUiEnabled.value =
          store.state?.creditLine?.summary?.isNewShipmentsUiEnabled;

        isGoogleAutocompleteNotEnabled.value = isFeatureEnabled(
          "GoogleAutocompleteNotEnabled",
          store?.state?.creditLine?.summary?.features ??
            store?.state?.creditLine?.summary?.experiments
        );

        return store.state.creditLine.summary as ICreditLineDetails;
      } catch (error) {
        ErrorHandler.handleNetworkErrorAndLog(error);
        throw error;
      } finally {
        isLoading.value = false;
      }
    })();

    return fetchPromise;
  };

  return {
    summary,
    isLoading,
    fetchSummary,
    doesHaveEnoughBalanceToWithdraw,
    lastFourDigits: computed(() => lastFourDigits.value),
    isNewShipmentsUiEnabled: computed(() => isNewShipmentsUiEnabled.value),
    isGoogleAutocompleteNotEnabled: computed(
      () => isGoogleAutocompleteNotEnabled.value
    ),
  };
}
